import React, { useState } from 'react'
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom'

import { AcornsIcon, Icon } from '@acorns/icons'
import { useAnalyticsClient, useRemoteStrings } from '@acorns/web-utils'

import { routes } from 'router/routes'

import usePortfolioData from '../hooks/usePortfolioData'
import { PortfolioInvestmentProduct } from '../types'
import {
  BitcoinSuccessScreenOneTimeInvestmentCta,
  BitcoinSuccessScreenRecurringInvestmentCta,
} from './analytics'
import BitcoinImage from './assets/bitcoin.svg'
import { CryptoLander } from './CryptoLander'
import {
  BitcoinLanderButtonTapped,
  BitcoinLanderModalButtonTapped,
  BitcoinOnboardingSuccessViewed,
} from './events'
import { Loading } from './Loading'
import { StyledValueProp, ValuePropText } from './styled-components'
import {
  SUCCESS_BUTTON_TYPE_PRIMARY,
  SUCCESS_BUTTON_TYPE_SECONDARY,
  Success,
  SuccessAction,
} from './Success'
import { useCrypto } from './useCrypto'

export const STRING_PREFIX = 'invest.new_etf.lander.'
const stringKeys = [
  'action_strip_title',
  'alert.add_to_portfolio.cta.cancel',
  'alert.add_to_portfolio.cta.confirm',
  'alert.add_to_portfolio.title',
  'body1',
  'body2.markdown',
  'cta',
  'header1',
  'header2',
  'loading.label',
  'success.body',
  'success.cta',
  'success.body.recurring',
  'success.body.one_time',
  'success.cta.one_time',
  'success.cta.recurring',
  'success.cta.alt',
  'success.title.markdown',
  'title',
  'value_prop1',
  'value_prop2',
  'value_prop3',
  'disclosure.link.markdown',
] as const

const iconKeys = [Icon.CryptoWhite, Icon.TransactionWhite, Icon.BinocularWhite]

type ValuesOf<T extends readonly any[]> = T[number]

type PageCopy = {
  [key in ValuesOf<typeof stringKeys>]: string
}

export const ValueProp = ({
  copy,
  iconName,
}: {
  copy: string
  iconName: Icon
}) => (
  <StyledValueProp>
    <AcornsIcon icon={iconName} height={24} width={24} />
    <ValuePropText>{copy}</ValuePropText>
  </StyledValueProp>
)

export type CryptoInterstitalProps = {
  onBack: () => void
  imageUrl: string
  icons: Icon[]
  toggleModal: () => void
  isModalOpen: boolean
  onAddPortfolioConfirmPress: () => void
  currentProductPortfolio: PortfolioInvestmentProduct
  onAddPortfolioPress: (cb?: () => void) => void
} & PageCopy

type LocationState<T extends { [key: string]: string }> = {
  state?: T
}

export const CryptoInterstitial: React.FunctionComponent<{}> = () => {
  const history = useHistory()
  const location = useLocation<LocationState<{ referrer: string }>>()
  const {
    cryptoEtf,
    isCryptoDataLoading,
    investmentAccount,
    eligibleForCrypto,
    isAddCryptoMutationSubmitting,
    addCryptoToPortfolio,
  } = useCrypto()
  const { currentProductPortfolio, hasRecurring } = usePortfolioData()
  const analytics = useAnalyticsClient()

  const investmentProduct = currentProductPortfolio.toLowerCase()

  const userDoesntBelong = !isCryptoDataLoading && !eligibleForCrypto

  if (userDoesntBelong) {
    history.replace(routes.invest.portfolio)
  }

  const onboardingSuccessScreenViewedEvent =
    BitcoinOnboardingSuccessViewed(investmentProduct)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const { getRemoteString } = useRemoteStrings()
  const referrer = location?.state?.referrer

  const fetchedStrings = stringKeys.map((string) =>
    getRemoteString({ key: `${STRING_PREFIX}${string}` }),
  )

  const strings = fetchedStrings.reduce((pageCopy, currentString, index) => {
    pageCopy[stringKeys[index]] = currentString
    return pageCopy
  }, {} as PageCopy)

  const onBack = () => {
    setIsModalOpen(false)
    if (referrer) {
      history.push(referrer)
    } else {
      history.push(routes.invest.portfolio)
    }
  }

  const toggleModal = () => {
    setIsModalOpen((isModalOpen) => !isModalOpen)
  }

  const onAddPortfolioPress = (cb?: () => void) => {
    analytics.track(
      'Button Tapped',
      BitcoinLanderButtonTapped(investmentProduct),
    )
    cb ? cb() : null
  }

  const onAddPortfolioConfirmPress = async () => {
    setIsModalOpen(false)
    analytics.track(
      'Button Tapped',
      BitcoinLanderModalButtonTapped(investmentProduct),
    )
    const response = await addCryptoToPortfolio({
      variables: {
        input: {
          symbol: cryptoEtf!.symbol,
          investmentAccountId: investmentAccount!.id,
        },
      },
    })

    if (response.data?.addSecurityToPortfolio.__typename === 'Portfolio') {
      history.push(routes.portfolio.crypto.success)
    }
  }

  if (isAddCryptoMutationSubmitting || isCryptoDataLoading) {
    return (
      <Loading
        title={
          isAddCryptoMutationSubmitting ? strings['loading.label'] : undefined
        }
      />
    )
  }

  const onSuccessPressed = () => {
    if (hasRecurring) {
      analytics.track('Button Tapped', BitcoinSuccessScreenOneTimeInvestmentCta)
      history.push(routes.invest.oneTimeDeposit)
    } else {
      analytics.track(
        'Button Tapped',
        BitcoinSuccessScreenRecurringInvestmentCta,
      )
      history.push(routes.invest.recurring, {
        referrer: routes.portfolio.crypto.success,
      })
    }
  }

  const handleLaterPressed = () => {
    history.push(routes.portfolio.invest)
  }

  const successActions: SuccessAction[] = [
    {
      text: hasRecurring
        ? strings['success.cta.one_time']
        : strings['success.cta.recurring'],
      cta: onSuccessPressed,
      type: SUCCESS_BUTTON_TYPE_PRIMARY,
    },
    {
      text: strings['success.cta.alt'],
      cta: handleLaterPressed,
      type: SUCCESS_BUTTON_TYPE_SECONDARY,
    },
  ]

  return (
    <Switch>
      <Route
        path={routes.portfolio.crypto.lander}
        render={() => (
          <CryptoLander
            icons={iconKeys}
            imageUrl={BitcoinImage}
            onBack={onBack}
            toggleModal={toggleModal}
            isModalOpen={isModalOpen}
            onAddPortfolioConfirmPress={onAddPortfolioConfirmPress}
            currentProductPortfolio={currentProductPortfolio}
            onAddPortfolioPress={onAddPortfolioPress}
            {...strings}
          />
        )}
      />
      <Route
        path={routes.portfolio.crypto.success}
        exact={true}
        render={() => (
          <Success
            body={
              hasRecurring
                ? strings['success.body.one_time']
                : strings['success.body.recurring']
            }
            screenViewed={onboardingSuccessScreenViewedEvent}
            actions={successActions}
          />
        )}
      />
      <Redirect to={routes.portfolio.invest} />
    </Switch>
  )
}
